<template>
  <div class="conversation" id="conversation" :style="{ height: conversationHeight }">
    <div
      v-if="isLoadingConversationVisible"
      class="message__system animate__animated animate__fadeInUp animate__fastest"
    >
      <div class="typing">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
    <div
      v-for="(message, messageIndex) in conversation"
      :key="message.id"
      :class="{'flex justify-end': message.role === AI_ROLE_USER}"
    >
      <div class="system-message__wrapper">
        <SystemMessage
          v-if="message.role === AI_ROLE_SYSTEM"
          :messageContent="message.content"
          :isPendingMessage="message.type === AI_SYSTEM_PENDING_MESSAGE_TYPE"
          :class="{'has-rating': canUseQuickRating}"
        />
        <InlineCSAT
          v-if="isQuickRatingVisible(message)"
          class="animate__animated animate__fadeInUp animate__fastest"
          :id="quickRatingUniqueId(messageIndex)"
          :hoverToShow="!isLastMessageSuccess(message)"
          :data="{user_prompt: userPrompt(messageIndex), feature_name: 'AI prompt-based generation'}"
          :tooltip="quickRatingTooltip"
          enableFeedback
        />
      </div>
      <AIPromptSuggestion
        v-if="isAIPromptSuggestionVisible"
        :promptSuggestions="promptSuggestions"
        @clickOnSuggestion="clickOnSuggestion"
      />
      <div
        v-if="message.role === AI_ROLE_USER"
        class="message__user-wrapper"
        :class="{'has-rating': canUseQuickRating}"
      >
        <AIRetryButton
          v-if="systemError && lastUserMessageIndex === messageIndex"
          :userRetryMessage="userRetryMessage"
          @clickOnAIRetryButton="clickOnAIRetryButton"
        />
        <UserMessage
          :message="message"
          :needUnderlined="message.type === AI_USER_DOC_TO_SLIDE_MESSAGE_TYPE"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import OpenAiConstants from '@/constant/openAI'

import AIPromptSuggestion from '@/components-v2/presentation-editor/side-right/right-nav/AIPromptSuggestion.vue'
import SystemMessage from '@/components-v2/presentation-editor/side-right/right-nav/SystemMessage.vue'
import UserMessage from '@/components-v2/presentation-editor/side-right/right-nav/UserMessage.vue'
import AIRetryButton from '@/components-v2/presentation-editor/side-right/right-nav/AIRetryButton.vue'
import InlineCSAT from '@/components-v2/common/csat/InlineCSAT.vue'

export default {
  components: {
    AIPromptSuggestion,
    SystemMessage,
    UserMessage,
    AIRetryButton,
    InlineCSAT,
  },
  props: {
    conversation: {
      type: Array,
      default: () => [],
    },
    promptSuggestions: {
      type: Array,
      default: () => [],
    },
    systemError: {
      type: Boolean,
      default: false,
    },
    lastUserMessageIndex: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      AI_ROLE_SYSTEM: OpenAiConstants.AI_ROLE_SYSTEM,
      AI_ROLE_USER: OpenAiConstants.AI_ROLE_USER,
      AI_SYSTEM_PENDING_MESSAGE_TYPE: OpenAiConstants.AI_SYSTEM_PENDING_MESSAGE_TYPE,
      AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE: OpenAiConstants.AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE,
      AI_USER_DOC_TO_SLIDE_MESSAGE_TYPE: OpenAiConstants.AI_USER_DOC_TO_SLIDE_MESSAGE_TYPE,
    }
  },
  computed: {
    ...mapGetters('inlineCSAT', ['canUseQuickRating']),

    aiConversationFooterHeight() {
      const footer = document.getElementById('ai-conversation-footer')
      return footer ? footer.offsetHeight : 0
    },

    conversationHeight() {
      return `calc(100% - ${this.aiConversationFooterHeight}px)`
    },

    isLoadingConversationVisible() {
      return !this.isConversationVisible && (!this.aiResponse || !this.aiResponse.length)
    },
    isAIPromptSuggestionVisible() {
      return this.conversation && this.conversation.length === 1
    },
    userRetryMessage() {
      return this.conversation && this.conversation.length && this.conversation[this.lastUserMessageIndex]
        && this.conversation[this.lastUserMessageIndex].content
    },

    isQuickRatingVisible() {
      return (message) => {
        if (!this.canUseQuickRating) return false
        return message?.role === this.AI_ROLE_SYSTEM && message?.type !== this.AI_SYSTEM_PENDING_MESSAGE_TYPE
      }
    },

    isLastMessageSuccess() {
      return (message) => {
        const lastMessageIndex = this.conversation?.length - 1
        const lastMessage = this.conversation?.[lastMessageIndex]

        return lastMessage?.type === this.AI_SYSTEM_ADDED_SLIDE_MESSAGE_TYPE &&
        message?.content === lastMessage?.content
      }
    },

    quickRatingTooltip() {
      return {
        like: this.$i18nPresenter.t('I like the result'),
        dislike: this.$i18nPresenter.t('I don’t like the result'),
      }
    },

    quickRatingUniqueId() {
      return (messageIndex) => {
        return `quick-rating-${messageIndex}}`
      }
    },
  },
  methods: {
    clickOnSuggestion(suggestion) {
      this.$emit('clickOnSuggestion', suggestion)
    },
    clickOnAIRetryButton(mode) {
      this.$emit('clickOnAIRetryButton', mode)
    },

    userPrompt(messageIndex) {
      const previousMessage = this.conversation?.[messageIndex - 1]

      return previousMessage && previousMessage.role === this.AI_ROLE_USER
        ? previousMessage.content
        : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.conversation {
  @apply flex-1 pt-4 px-4 w-full overflow-y-auto;
}
.message__user-wrapper {
  @apply flex items-center;
  &.has-rating::v-deep {
    .message__user {
      @apply mb-5;
    }
  }
}
.system-message__wrapper {
  .has-rating {
    @apply mb-1;
  }
  &:hover {
    &::v-deep {
      .is-invisible, .thumb-down__wrapper {
        @apply opacity-100;
      }
    }
  }
}
</style>
